import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import api from '../api';
import { OrganizationDto } from '../api-client';
import { QueryKey } from '../enums';

interface AcceptTermsOfServicePayload {
  organizationId: string;
}

export const useAcceptTermsOfService = (
  options?: UseMutationOptions<
    OrganizationDto,
    Error,
    AcceptTermsOfServicePayload,
    { previousOrganization?: OrganizationDto }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({ organizationId }: AcceptTermsOfServicePayload) =>
      api.organization.organizationControllerAcceptMicrositeTermsOfService(
        organizationId,
        { withAccessToken: true }
      ),
    onMutate: async variables => {
      const queryKey = [
        QueryKey.Resource.Organization,
        variables.organizationId,
      ];

      await queryClient.cancelQueries({ queryKey });

      const previousOrganization =
        queryClient.getQueryData<OrganizationDto>(queryKey);

      queryClient.setQueryData<OrganizationDto>(queryKey, old =>
        old ? { ...old, termsOfServiceAccepted: true } : old
      );

      options?.onMutate?.(variables);

      return { previousOrganization };
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Organization, variables.organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.SavingsSummary, variables.organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.SpendAreaSavings,
          variables.organizationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.SpendArea,
          QueryKey.Operation.List,
          variables.organizationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.ActionItem, QueryKey.Operation.List],
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (context?.previousOrganization) {
        queryClient.setQueryData<OrganizationDto>(
          [QueryKey.Resource.Organization, variables.organizationId],
          context.previousOrganization
        );
      }
      options?.onError?.(error, variables, context);
    },
  });
};
